import { TYPE_ANALYTICS_MAP_VIEW } from 'common/defines/constants';
import SkeletonCharts from 'components/Common/SkeletonCharts';
import ChartsAnalytics from 'components/MapView/RightBar/AnalyticsTab/Category/ChartsAnalytics/ChartsAnalytics';
import { CircumferenceCardLayer } from 'components/MapView/RightBar/AnalyticsTab/Layer/Circumference';
import { SwipeableViewsCustom } from 'components/MapView/RightBar/AnalyticsTab/SwipeableViewsCustom';
import { QUERY_KEY } from 'constants/constants';
import { get, isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { updateInterval } from 'services/analytics/apiAnalyticsConfig.services';
import {
  getChartsWorkspaceCircumference,
  getDataWorkspaceCircumferenceAnalytic,
  getWorkspaceCircumferenceBoxPlot,
  getWorkspaceCircumferencePolygon,
} from 'services/workspaces';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  changeChartDataCircumferencePreview,
  changeDataCircumferenceAnalytics,
  changeDataCircumferenceInterval,
  changeDataPolygonCircumferenceAnalytics,
  circumferenceAnalyticsSelector,
} from 'store/slices/map-view/circumferenceAnalytics';
import { tilingMapSelector } from 'store/slices/tilingMapSlice';

const CircumferenceAnalytic = () => {
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { analysisId } = useAppSelector(tilingMapSelector);
  const { dataCircumferenceInterval, dataCircumferencePolygon, chartDataCircumferencePreview } =
    useAppSelector(circumferenceAnalyticsSelector);
  const isDefaultInterval = true;

  const { data: dataCircumferenceBoxPlot, isLoading } = useQuery(
    [QUERY_KEY.WORKSPACE_CIRCUMFERENCE_BOX_PLOT, analysisId],
    () => getWorkspaceCircumferenceBoxPlot(analysisId),
    { enabled: !!analysisId }
  );

  useQuery(
    [QUERY_KEY.WORKSPACE_CIRCUMFERENCE_POLYGON, analysisId, currentPage],
    () => getWorkspaceCircumferencePolygon(analysisId, currentPage),
    {
      enabled: !!analysisId,
      onSuccess: (data: any) => {
        if (data.data?.nextPage && data.data.nextPage !== currentPage) {
          setCurrentPage(data.data?.nextPage);
        }
        const circumferencePolygonList = currentPage === 1 ? [] : dataCircumferencePolygon;
        const polygonListRes = data.data?.docs;
        if (polygonListRes) {
          const convertedPolygonList = polygonListRes.map((item: any) => ({
            ...item,
            diaLength: item.diaLength,
            cirLength: item.cirLength,
          }));
          dispatch(changeDataPolygonCircumferenceAnalytics([...circumferencePolygonList, ...convertedPolygonList]));
        }
      },
    }
  );

  useQuery(
    [QUERY_KEY.WORKSPACE_CIRCUMFERENCE_ANALYTIC, analysisId],
    () => {
      return getDataWorkspaceCircumferenceAnalytic(analysisId);
    },
    {
      enabled: !!analysisId,
      onSuccess: (res) => {
        const convertedData = res.data.map((item: any) => ({ ...item, longX: item.long, latY: item.lat }));
        dispatch(changeDataCircumferenceAnalytics(convertedData || []));
      },
    }
  );

  const handleUpdateIntervals = useMutation(
    (data: any) => updateInterval(chartsData?.data?.intervalLimit?._id || '', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEY.CHARTS_CIRCUMFERENCE_ANALYTIC, isDefaultInterval]).then(() => {});
      },
    }
  );

  const { data: chartsData } = useQuery(
    [QUERY_KEY.CHARTS_WORKSPACE_CIRCUMFERENCE_ANALYTIC, isDefaultInterval],
    () => getChartsWorkspaceCircumference(analysisId, isDefaultInterval),
    {
      enabled: !!analysisId,
      onSuccess: (data) => {
        dispatch(
          changeDataCircumferenceInterval({
            ...data.data?.intervalLimit,
            range: data.data?.intervalLimit.range.map((range: any, index: number) => ({
              ...range,
              visible: get(dataCircumferenceInterval, `range[${index}].visible`, true),
            })),
          })
        );
      },
    }
  );

  const dataBoxPlot = useMemo(() => {
    if (!dataCircumferenceBoxPlot?.data) return [];
    return dataCircumferenceBoxPlot?.data;
  }, [dataCircumferenceBoxPlot]);

  return (
    <SwipeableViewsCustom
      analyticName={TYPE_ANALYTICS_MAP_VIEW.CIRCUMFERENCE_ANALYSIS}
      analyticTab={
        <>
          {!isLoading ? (
            <>
              {!isEmpty(chartsData?.data?.chartData) && (
                <ChartsAnalytics
                  dataChart={
                    chartDataCircumferencePreview
                      ? chartDataCircumferencePreview?.chartData
                      : chartsData?.data?.chartData || []
                  }
                  title={TYPE_ANALYTICS_MAP_VIEW.CIRCUMFERENCE_ANALYSIS}
                  getCharts={getChartsWorkspaceCircumference}
                  intervalRange={
                    chartDataCircumferencePreview
                      ? chartDataCircumferencePreview?.intervalLimit?.range
                      : chartsData?.data?.intervalLimit?.range || []
                  }
                  handleUpdateIntervals={handleUpdateIntervals}
                  queryKey={QUERY_KEY.CHARTS_CIRCUMFERENCE_ANALYTIC}
                  dataBoxPlot={dataBoxPlot}
                  changeChartDataPreview={changeChartDataCircumferencePreview}
                  isInWorkspace
                />
              )}
            </>
          ) : (
            <SkeletonCharts count={1} />
          )}
        </>
      }
      layerTab={<CircumferenceCardLayer labelCard={TYPE_ANALYTICS_MAP_VIEW.CIRCUMFERENCE_ANALYSIS} isInTilingMap />}
      isShowDownloadButton={!isLoading && !isEmpty(chartsData?.data?.chartData)}
      isInWorkspace
    />
  );
};

export default CircumferenceAnalytic;
